import { useDispatch, useSelector } from 'react-redux'
import { selectActiveWidget, selectLayout, selectLayoutPanes, selectLayoutWidgets, selectSyncSettings } from '../../selectors/widgetDashboard'
import { DashboardEventData, DashboardSyncEvents, DashboardSyncSettings, IWidget, WidgetConfig, WidgetDashboardProps } from './widgetDashboard.interface'
import MultiPaneLayout from '../multiPane/multiPaneLayout/multiPaneLayout'
import { addWidget, deleteWidget, setActiveWidget, updateLayoutPanes, updateWidgets } from '../../reducer/widgetDashboard.reducer'
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'throttle-debounce'
import { IMultiPaneLayout, MultiPaneItem, MultiPanesConfig } from '../multiPane/multiPaneLayout.interface'
import SlotContainer from '../multiPane/slotContainer'
import { findActivePaneOnDelete, generatePaneLayoutsMap, getDefaultPane } from '../multiPane/multipPaneLayout.helper'
import { WidgetTypes } from './widgetDashboard.enums'
import { getDefaultWidget, getWidgetComponent } from './widgetDashboard.helper'
import { getSecurityKey } from '../../util/security'
import { AppContext } from '../../App'
import { ShortcutsCategories, ShortcutType } from '../KeyboardShortcuts/Shortcuts.types'
import { ChartShortcutsKeyName } from '../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../hooks/useShortcuts'
import { selectActiveChartId } from '../../selectors/chart'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import { setMultiPaneFullScreen } from '../../actions/common.actions'
import { SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { useTheme } from '@mui/material'
import DashboardPaneBackground from '../common/DashboardPaneBackground'

const DUMMY_PANE = getDefaultPane()

const WidgetDashboard: React.FC<WidgetDashboardProps> = ({ id, allowInitialEmpty = true, maxPanes, tradeOptions, disableSplit }) => {
    const { push, undo, redo } = useUndoRedoContext()
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const chart = appContext.getChart(activeChartId)
    const layout = useSelector(selectLayout(id))
    const panes = useSelector(selectLayoutPanes(id))
    const widgets = useSelector(selectLayoutWidgets(id))
    const widgetRefs = useRef<Record<number, IWidget>>({})
    const paneLayoutRef = useRef<IMultiPaneLayout>(null)
    const activeWidget = useSelector(selectActiveWidget(id))
    const activeWidgetRef = useRef(activeWidget)
    const previousWidgets = useRef<Record<number, WidgetConfig<any>>>({})
    const syncSettings = useSelector(selectSyncSettings(id))
    const dashboardSyncSettings = useRef<DashboardSyncSettings>(syncSettings)
    const mounted = useRef(false)
    const panesConfig = useMemo(() => (panes ? generatePaneLayoutsMap(panes) : {}), [panes])
    const isPlaceholderMode = !layout
    const effectivePanes = panes ?? DUMMY_PANE
    const effectivePanesConfig = useMemo(
        () => (panes ? panesConfig : generatePaneLayoutsMap(DUMMY_PANE)),
        [panes, panesConfig]
    )
    const previousKey = useRef(activeWidget?.settings?.key || '')
    const [hoveredPane, setHoveredPane] = useState<number>(NaN)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const hasPushedRef = useRef(false)
    const visiblePanes = useMemo(() => Object.values(effectivePanesConfig).filter((pane) => pane.panes.length === 0), [effectivePanesConfig])
    const allowSplit = useMemo(() => !disableSplit && !isPlaceholderMode && (!maxPanes || visiblePanes.length < maxPanes), [isPlaceholderMode, visiblePanes, maxPanes, disableSplit])
    const theme = useTheme()
    useEffect(() => {
        if (!hasPushedRef.current && layout) {
            push(layout, 'Initial Data', ActionType.UPDATE_LAYOUT)
            hasPushedRef.current = true
        }
    }, [layout])
    useEffect(() => {
        activeWidgetRef.current = activeWidget
    }, [activeWidget])
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isInFullScreen = !!document.fullscreenElement
            setIsFullScreen(isInFullScreen)
            dispatch(setMultiPaneFullScreen(isInFullScreen))
            if (!isInFullScreen) {
                setTimeout(() => {
                    const element = document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)
                    if (element) {
                        element.style.height = '100%'
                        element.style.overflow = 'hidden'
                    }
                    ;(chart as any)?.resize?.()
                }, 100)
            }
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
        }
    }, [dispatch, chart])
    const shortcuts: ShortcutType = useMemo(
        () => ({
            [ChartShortcutsKeyName.showHideFullScreen]: () => {
                const element = document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)
                if (element) {
                    if (!isFullScreen) {
                        AnalyticsManager.logEvent('Show Full Screen', { source: 'keyboard' })
                        element
                            .requestFullscreen()
                            .then(() => {
                                dispatch(setMultiPaneFullScreen(true))
                                setIsFullScreen(true)
                            })
                            .catch((err) => {
                                console.error(`Error entering fullscreen: ${err.message}`)
                            })
                    } else {
                        AnalyticsManager.logEvent('Exit Full Screen', { source: 'keyboard' })
                        document
                            .exitFullscreen()
                            .then(() => {
                                dispatch(setMultiPaneFullScreen(false))
                                setIsFullScreen(false)
                            })
                            .catch((err) => {
                                console.error(`Error exiting fullscreen: ${err.message}`)
                            })
                    }
                } else {
                    // Exit fullscreen
                    AnalyticsManager.logEvent('Exit Full Screen', { source: 'keyboard' })
                    document
                        .exitFullscreen()
                        .then(() => {
                            dispatch(setMultiPaneFullScreen(false))
                        })
                        .catch((err) => {
                            console.error(`Error exiting fullscreen: ${err.message}`)
                        })
                }
            },
            [ChartShortcutsKeyName.resetChartView]: () => {
                AnalyticsManager.logEvent('Reset Chart View', { source: 'keyboard' })
                chart.resetTimeScale()
            }
        }),
        [chart, dispatch, isFullScreen]
    )

    useShortcuts(ShortcutsCategories.CHART, shortcuts)

    useEffect(() => {
        previousWidgets.current = {}
        mounted.current = false
        previousKey.current = ''
        appContext.setDashboard({
            getActiveWidget() {
                return activeWidgetRef.current
            },
            splitPane(paneId, numberOfDivisions, horizontal) {
                dispatch(setActiveWidget({ id, slotId: 0 })) // reset active state to avoid highlight flicker
                paneLayoutRef.current && paneLayoutRef.current.splitPane(paneId, numberOfDivisions, horizontal)
            },
            deletePane(paneId) {
                paneLayoutRef.current && paneLayoutRef.current.deletePane(paneId)
            },
            updateWidgetConfig(slotId, settings) {
                const widget = previousWidgets.current[slotId]

                dispatch(
                    updateWidgets({
                        id,
                        widgets: {
                            ...previousWidgets.current,
                            [slotId]: {
                                ...widget,
                                settings
                            }
                        }
                    })
                )
            },
            updateSecurity(key, expressionMode, securities, displayName?: string) {
                if (previousKey.current !== key && activeWidgetRef.current && activeWidgetRef.current.settings.key !== key && widgetRefs.current[activeWidgetRef.current.slotId]) {
                    widgetRefs.current[activeWidgetRef.current.slotId]?.updateSettings({
                        key,
                        expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        displayName
                    })
                }
            },
            broadcast(id, event, data, forced = false) {
                Object.keys(widgetRefs.current).forEach((wId) => {
                    const widgetId = parseInt(wId)
                    if (widgetId === id) return
                    const widget = widgetRefs.current[widgetId]
                    if (!widget) return
                    switch (event) {
                        case DashboardSyncEvents.SECURITY:
                            if (dashboardSyncSettings.current.security || forced) {
                                widget.onSecurityUpdate && widget.onSecurityUpdate(data as DashboardEventData[DashboardSyncEvents.SECURITY])
                            }
                            break
                        case DashboardSyncEvents.CROSSHAIR:
                            if (dashboardSyncSettings.current.crosshair || forced) {
                                widget.onCrosshairUpdate && widget.onCrosshairUpdate(data as DashboardEventData[DashboardSyncEvents.CROSSHAIR])
                            }
                            break
                        case DashboardSyncEvents.TIME:
                            if (dashboardSyncSettings.current.time || forced) {
                                widget.onTimeUpdate && widget.onTimeUpdate(data as DashboardEventData[DashboardSyncEvents.TIME])
                            }
                            break
                        case DashboardSyncEvents.DATE:
                            if (dashboardSyncSettings.current.time || forced) {
                                widget.onDateUpdate && widget.onDateUpdate(data as DashboardEventData[DashboardSyncEvents.DATE])
                            }
                            break
                        case DashboardSyncEvents.TIME_BUCKET:
                            if (dashboardSyncSettings.current.timeBucket || forced) {
                                widget.onTimeBucketUpdate && widget.onTimeBucketUpdate(data as DashboardEventData[DashboardSyncEvents.TIME_BUCKET])
                            }
                            break
                        case DashboardSyncEvents.DRAWING:
                            if (dashboardSyncSettings.current.drawing || forced) {
                                widget.onDrawingUpdate && widget.onDrawingUpdate(data as DashboardEventData[DashboardSyncEvents.DRAWING])
                            }
                            break
                    }
                })
            }
        })
    }, [id])

    const focusWidget = useCallback(
        (slotId: number) => {
            if (activeWidget && activeWidget.slotId === slotId && activeChartId === activeWidget.settings.chartId && mounted.current) return
            const ref = widgetRefs.current[slotId]
            dispatch(setActiveWidget({ id, slotId }))
            const currentlyActiveWidget = activeWidget ? widgetRefs.current[activeWidget.slotId] : null
            currentlyActiveWidget && currentlyActiveWidget.onBlur()
            ref && ref.onActive()
        },
        [activeWidget, dispatch, id, activeChartId]
    )

    useEffect(() => {
        if (layout && !mounted.current) {
            mounted.current = true
        }
    }, [widgets, layout])

    const onPaneDeleted = useCallback(
        (slotId: number, oldPanesConfig: MultiPanesConfig) => {
            widgetRefs.current[slotId].onRemove()
            const newActiveId = findActivePaneOnDelete(slotId, oldPanesConfig)
            focusWidget(newActiveId)
            dispatch(
                deleteWidget({
                    id,
                    slotId
                })
            )
        },
        [focusWidget, dispatch, id]
    )

    useEffect(() => {
        if (!syncSettings || !dashboardSyncSettings.current || !activeWidget) return
        const prevSettings = { ...dashboardSyncSettings.current }
        const widget = widgetRefs.current[activeWidget?.slotId]
        if (!widget) return
        dashboardSyncSettings.current = syncSettings

        if (syncSettings.security && !prevSettings.security) {
            widget.onSyncSettingsChanged('security', syncSettings.security)
        }
        if (syncSettings.timeBucket && !prevSettings.timeBucket) {
            widget.onSyncSettingsChanged('timeBucket', syncSettings.timeBucket)
        }
    }, [syncSettings, appContext, activeWidget])

    const onPaneAdded = useCallback(
        (slotId: number) => {
            if (!activeWidgetRef.current) return

            if (!allowInitialEmpty) {
                const settings = activeWidgetRef.current.settings
                let timeBucket: SuperChartConfiguration_TimeBucket | undefined = undefined
                if (activeWidgetRef.current.widgetType === WidgetTypes.CHART) {
                    const currentChart = appContext.getChart(settings.chartId)
                    timeBucket = currentChart?.chartConfig?.timeBucket
                }
                dispatch(
                    addWidget({
                        id,
                        widget: getDefaultWidget(id, slotId, WidgetTypes.CHART, settings.key, settings.expressionMode, settings.securities, timeBucket)
                    })
                )
            }
        },
        [allowInitialEmpty, dispatch, id, appContext]
    )

    useEffect(() => {
        const previousWidgetIds = Object.keys(previousWidgets.current)
            .map((v) => parseInt(v))
            .sort()
        const currentWidgetIds = Object.keys(widgets)
            .map((v) => parseInt(v))
            .sort()

        if (currentWidgetIds.length > previousWidgetIds.length || currentWidgetIds.length < previousWidgetIds.length) {
            const [activeSlot] = currentWidgetIds.slice(-1)
            mounted.current && focusWidget(activeSlot)
        }
        previousWidgets.current = widgets
    }, [focusWidget, widgets])

    const updateStoreLayout = useCallback(
        debounce(50, (newLayout: MultiPaneItem) => {
            dispatch(updateLayoutPanes({ id, panes: newLayout }))
        }),
        [id]
    )

    const resetHoveredPane = useCallback(() => {
        setHoveredPane(NaN)
    }, [])

    const widgetNodes: ReactNode[] = useMemo(() => {
        return Object.values(effectivePanesConfig).map((pane) => {
            const { id: paneId, panes: childPanes } = pane
            const widgetConfig = widgets[paneId]
            if (childPanes.length) return null
            return (
                <SlotContainer paneId={paneId} key={paneId} active={!!(activeWidget && activeWidget.slotId === paneId)}>
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            background: widgetConfig
                                ? theme.palette.background.paper
                                : theme.palette.background.default
                        }}
                        className="disable-selection"
                        onMouseDown={() => {
                            focusWidget(paneId)
                        }}
                        onTouchStart={() => {
                            focusWidget(paneId)
                        }}
                        onMouseEnter={() => setHoveredPane(Number.isFinite(widgetConfig?.slotId) ? widgetConfig?.slotId : paneId)}
                    >
                        {widgetConfig ? (
                            <div key={`${paneId}-${widgetConfig.widgetType}`} style={{ width: '100%', height: '100%' }}>
                                {getWidgetComponent(id, widgetConfig, widgetRefs.current, hoveredPane, allowSplit, tradeOptions)}
                            </div>
                        ) : isPlaceholderMode ? <DashboardPaneBackground /> : null}
                    </div>
                </SlotContainer>
            )
        })
    }, [effectivePanesConfig, widgets, activeWidget, id, focusWidget, hoveredPane, allowSplit, tradeOptions, isPlaceholderMode])
    if (layout && Object.values(panesConfig).length && !Object.values(widgets).length && !allowInitialEmpty) {
        return <DashboardPaneBackground />
    }

    if (!id) {
        return <DashboardPaneBackground />
    }

    return (
        <div
            id={MULTIPANE_FULLSCREEN_MODE_ID}
            style={{
                height: isFullScreen ? '100vh' : '100%',
                flex: 1,
                minHeight: 0,
                overflow: isFullScreen ? 'visible' : 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: theme.palette.background.default
            }}
        >
                    <MultiPaneLayout
                        layout={effectivePanes}
                        maxPanes={maxPanes}
                        onLayoutChange={isPlaceholderMode ? () => {} : updateStoreLayout}
                        ref={paneLayoutRef}
                        hidePaneActionButtons
                        onPaneAdded={onPaneAdded}
                        onMouseLeave={resetHoveredPane}
                        onPaneDeleted={onPaneDeleted}
                    >
                        {widgetNodes}
                    </MultiPaneLayout>
                </div>
    )
}

export default WidgetDashboard
